/**
 * Tour progress persistence (#776). localStorage when available; every access is wrapped because storage can be
 * missing or throw (private mode, blocked site data). Without storage an in-memory copy keeps the tour from
 * re-appearing within the session; a reload then shows it again, which is the best we can do.
 */
import { parsePersisted, type PersistedTour } from './machine';

export const TOUR_STORAGE_KEY = 'outfox.tour.v1';

let memory: string | null = null;

type Store = Pick<Storage, 'getItem' | 'setItem'>;

function defaultStore(): Store | undefined {
  try {
    return typeof window !== 'undefined' ? window.localStorage : undefined;
  } catch {
    return undefined;
  }
}

export function loadTour(store: Store | undefined = defaultStore()): PersistedTour | null {
  let raw: string | null = null;
  try {
    raw = store?.getItem(TOUR_STORAGE_KEY) ?? null;
  } catch {
    raw = null;
  }
  return parsePersisted(raw ?? memory);
}

export function saveTour(p: PersistedTour, store: Store | undefined = defaultStore()): void {
  const raw = JSON.stringify(p);
  memory = raw;
  try {
    store?.setItem(TOUR_STORAGE_KEY, raw);
  } catch {
    /* storage unavailable: the in-memory copy still applies for this session */
  }
}

/** Test hook: forget the in-memory fallback. */
export function resetTourMemory(): void {
  memory = null;
}
